import { useDraggable } from "@dnd-kit/core";
import { GridRow, GridRowProps } from "@mui/x-data-grid/components/GridRow";
import React, { useMemo } from "react";

import { TrackingDragPayload } from "@/stores/use-tracking-dnd";

const DraggableTrackingPassengerRow = React.forwardRef<HTMLDivElement, GridRowProps>(
    function DraggableTrackingPassengerRow(props, outerRef) {
        const isPlaceholder = Boolean(props.row?._isPlaceholder);
        const passengerId = String(props.row?.id ?? "");
        // Don't trust the passenger's own `taskId` field — use the id of the task this row is
        // actually rendered under (`_taskId`, injected by TrackingDetailTable), so the same-task
        // drop guard can't be defeated by a stale/mismatched value from the API.
        const taskId = String(props.row?._taskId ?? "");

        const dragData: TrackingDragPayload = useMemo(
            () => ({
                id: passengerId,
                name: props.row?.name ?? "",
                taskId,
                isFlightArrival: Boolean(props.row?.isFlightArrival),
            }),
            [passengerId, props.row?.name, props.row?.isFlightArrival, taskId]
        );

        const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
            id: isPlaceholder ? `__placeholder_task__${taskId}` : passengerId,
            disabled: isPlaceholder,
            data: dragData,
        });

        const mergedRef = (el: HTMLDivElement | null) => {
            if (!isPlaceholder) setNodeRef(el);
            if (typeof outerRef === "function") outerRef(el);
            else if (outerRef) (outerRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
        };

        if (isPlaceholder) {
            return (
                <GridRow
                    ref={(el: HTMLDivElement | null) => {
                        if (typeof outerRef === "function") outerRef(el);
                        else if (outerRef) (outerRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
                    }}
                    {...props}
                    style={{
                        ...props.style,
                        opacity: 0.5,
                        backgroundColor: "rgba(20,184,166,0.06)",
                        outline: "1.5px dashed rgba(20,184,166,0.5)",
                        outlineOffset: "-2px",
                        pointerEvents: "none",
                    }}
                />
            );
        }

        return (
            <GridRow
                ref={mergedRef}
                {...props}
                {...attributes}
                {...listeners}
                style={{
                    ...props.style,
                    opacity: isDragging ? 0 : 1,
                    cursor: "grab",
                    transition: isDragging ? undefined : "opacity 150ms ease",
                    userSelect: "none",
                }}
            />
        );
    }
);

export default DraggableTrackingPassengerRow;
